import { useDebounceEffect } from 'ahooks';
import { IGlobalSearchResultGroup, IGlobalSearchSuggestionGroup } from 'odc-shared/components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ModuleSearchPostsReq, ModuleSupportCenterPost } from '@src/api/generated/data-contracts';
import { postSupportSearch } from '@src/api/generated/Support';
import useMobxActions from '@src/hooks/useMobxActions';
import useMobxSelector from '@src/hooks/useMobxSelector';
import useNav from '@src/hooks/useNav';
import { getCategoryFirstPid } from '@src/utils/helpCenter';

import { BASE_LANGUAGE, buildLanguageQuery } from '../constants/language';

// 搜索相关类型定义
export interface SearchState {
  keyword: string;
  results: ModuleSupportCenterPost[];
  loading: boolean;
  selectedCategory: string;
  total: number;
}

interface SearchActions {
  setKeyword: (keyword: string) => void;
  setSelectedCategory: (category: string) => void;
  performSearch: (keyword: string) => Promise<void>;
}

export interface SupportSearchReturn {
  searchState: SearchState;
  searchActions: SearchActions;
  searchSuggestions: IGlobalSearchSuggestionGroup[];
  searchResults: IGlobalSearchResultGroup[];
  showGlobalSearch: boolean;
  setShowGlobalSearch: (show: boolean) => void;
  handleSearchChange: (e: unknown, value: string) => void;
  handleSearchClose: () => void;
  handleViewMore: () => void;
}

/**
 * @param language 当前内页内容语言：内页搜索按该语言检索，不做跨语言混搜
 */
function useSupportSearch(language: string = BASE_LANGUAGE): SupportSearchReturn {
  const actions = useMobxActions();
  const nav = useNav();

  // 搜索相关状态
  const [searchState, setSearchState] = useState<SearchState>({
    keyword: '',
    results: [],
    loading: false,
    selectedCategory: 'all',
    total: 0,
  });

  // 全局搜索相关
  const [showGlobalSearch, setShowGlobalSearch] = useState<boolean>(false);

  const catalogsByPlatform = useMobxSelector((stores) => stores.helpCenterStore.catalogsByPlatform);
  // 已缓存目录对应的语言：判断建议列表是不是当前语言那一份（P7）
  const catalogsLanguage = useMobxSelector((stores) => stores.helpCenterStore.catalogsLanguage);

  // 详情页链接上的语言参数：基准语言时为空串，保持链接与改造前一致
  const langQuery = useMemo(() => buildLanguageQuery(language), [language]);

  // 组件挂载时请求数据；切语言后目录名要跟着变，language 必须进依赖
  useEffect(() => {
    // 请求两个平台的目录结构；错误提示跟随帮助中心的 i18n
    actions.helpCenterStore.fetchCatalogs('publisher', { language, i18nErrorText: true });
    actions.helpCenterStore.fetchCatalogs('advertiser', { language, i18nErrorText: true });
    // 热门文章只在首页展示、固定英文，这里不传语言；错误提示同样跟随 i18n
    actions.helpCenterStore.fetchPopularPosts(false, true);
  }, [actions, language]);

  // 搜索请求序号：同一时刻只认最新一次搜索的结果
  const searchSeqRef = useRef(0);

  // 搜索函数
  const performSearch = useCallback(async (keyword: string) => {
    // 请求序号：快速输入或切语言时，先发出去的旧请求可能后返回，
    // 不加判断会用旧关键词的结果盖掉新结果
    const seq = ++searchSeqRef.current;

    if (!keyword.trim()) {
      // 关键词清空：结果与总数一起归零（只清结果会让"共 N 条"停在旧值）
      setSearchState(prev => ({ ...prev, results: [], total: 0, loading: false }));
      return;
    }

    setSearchState(prev => ({ ...prev, loading: true }));
    try {
      const searchParams: ModuleSearchPostsReq = {
        keyword: keyword.trim(),
        page: 0,
        pageSize: 20,
        withContent: true,
        searchType: 'both',
        language,
      };

      const response = await postSupportSearch(searchParams);

      // 已有更新的搜索发出，丢弃本次结果
      if (seq !== searchSeqRef.current) return;

      if (response.code === 0 && response.data?.items) {
        setSearchState(prev => ({
          ...prev,
          results: response.data.items || [],
          loading: false,
          total: response.data.pageInfo?.totalNumber || 0,
        }));
      } else {
        console.error('Search failed:', response.msg);
        setSearchState(prev => ({ ...prev, results: [], total: 0, loading: false }));
      }
    } catch (error) {
      if (seq !== searchSeqRef.current) return;
      console.error('Search error:', error);
      setSearchState(prev => ({ ...prev, results: [], total: 0, loading: false }));
    }
  }, [language]);

  useDebounceEffect(
    () => {
      if (searchState.keyword) {
        performSearch(searchState.keyword);
      } else {
        searchSeqRef.current += 1; // 作废在途请求，避免它返回后又把结果写回列表
        setSearchState(prev => ({ ...prev, results: [], total: 0 }));
      }
    },
    // 切语言要按新语言重搜
    [searchState.keyword, language],
    {
      wait: 300,
    },
  );

  // 生成搜索建议 - 使用两个平台的一级目录
  const searchSuggestions = useMemo(() => {
    // 目录数据还不是当前语言那一份（刚切语言/刚从首页进内页）时不给建议：
    // 否则弹窗里会短暂露出上一种语言的目录名（P7）
    if (catalogsLanguage.publisher !== language && catalogsLanguage.advertiser !== language) {
      return [];
    }

    const publisherCatalogs = catalogsByPlatform.publisher || [];
    const advertiserCatalogs = catalogsByPlatform.advertiser || [];

    const groups = [];

    // User Growth 平台建议
    if (advertiserCatalogs.length > 0) {
      groups.push({
        title: 'User Growth',
        suggestions: advertiserCatalogs.map((catalog, index) => ({
          id: `advertiser_${catalog.id || index}`,
          label: catalog.name || 'Untitled',
          url: `/supportcenter?pid=${getCategoryFirstPid(catalog)}&platform=advertiser${langQuery}`,
        })),
      });
    }

    // Monetization 平台建议
    if (publisherCatalogs.length > 0) {
      groups.push({
        title: 'Monetization',
        suggestions: publisherCatalogs.map((catalog, index) => ({
          id: `publisher_${catalog.id || index}`,
          label: catalog.name || 'Untitled',
          url: `/supportcenter?pid=${getCategoryFirstPid(catalog)}&platform=publisher${langQuery}`,
        })),
      });
    }

    return groups;
  }, [catalogsByPlatform, catalogsLanguage, language, langQuery]);

  // 转换搜索结果为组件需要的格式
  const searchResults = useMemo((): IGlobalSearchResultGroup[] => {
    if (searchState.results.length === 0) return [];

    // 按平台分组
    const publisherResults = searchState.results.filter(post => post.platform === 'publisher');
    const advertiserResults = searchState.results.filter(post => post.platform === 'advertiser');

    const groups: IGlobalSearchResultGroup[] = [];

    if (advertiserResults.length > 0) {
      // 如果有另一个平台的数据，展示2个，反之，展示4个
      const limit = publisherResults.length > 0 ? 2 : 4;
      groups.push({
        title: 'User Growth',
        results: advertiserResults.slice(0, limit).map((post, index) => ({
          id: `advertiser_${post.id || index}`,
          label: post.title || 'Untitled',
          url: `/supportcenter?pid=${post.id}&platform=advertiser${langQuery}`,
        })),
      });
    }

    if (publisherResults.length > 0) {
      // 如果有另一个平台的数据，展示2个，反之，展示4个
      const limit = advertiserResults.length > 0 ? 2 : 4;
      groups.push({
        title: 'Monetization',
        results: publisherResults.slice(0, limit).map((post, index) => ({
          id: `publisher_${post.id || index}`,
          label: post.title || 'Untitled',
          url: `/supportcenter?pid=${post.id}&platform=publisher${langQuery}`,
        })),
      });
    }

    return groups;
  }, [searchState.results, langQuery]);

  // 搜索操作
  const searchActions: SearchActions = useMemo(() => ({
    setKeyword: (keyword: string) => {
      setSearchState(prev => ({ ...prev, keyword }));
    },
    setSelectedCategory: (category: string) => {
      setSearchState(prev => ({ ...prev, selectedCategory: category }));
    },
    performSearch,
  }), [performSearch]);

  // 处理搜索框变化
  const handleSearchChange = useCallback((_e: unknown, value: string) => {
    searchActions.setKeyword(value);
  }, [searchActions]);

  // 处理搜索框关闭
  const handleSearchClose = useCallback(() => {
    setShowGlobalSearch(false);
    searchActions.setKeyword('');
  }, [searchActions]);

  // 处理查看更多 - 跳转到首页的「全部结果页」，保持关键词与语言
  const handleViewMore = useCallback(() => {
    setShowGlobalSearch(false);
    const searchParams = new URLSearchParams();
    searchParams.set('current', 'support');
    searchParams.set('allResults', 'true');
    searchParams.set('keyword', searchState.keyword);
    // 语言必须显式写进 URL：全部结果页要"跟随发起端"，
    // 而首页自身固定英文，不能让对端去读 localStorage 猜语言
    searchParams.set('language', language);
    nav.to('/', {
      search: Object.fromEntries(searchParams),
    });
  }, [nav, searchState.keyword, language]);

  return {
    searchState,
    searchActions,
    searchSuggestions,
    searchResults,
    showGlobalSearch,
    setShowGlobalSearch,
    handleSearchChange,
    handleSearchClose,
    handleViewMore,
  };
}

export default useSupportSearch;